/**
 * helix-galaxy-data.ts — Live Supabase data hook for the Helix Galaxy
 * (HelixScoreDemo) at /helix-galaxy.
 *
 * Joins:
 *   - account_cp_status  → CP1-CP10 status per account
 *   - account_answers    → answered question_keys + selected_option
 *   - shared/questions   → question catalog (text, options, points)
 *
 * Tenant isolation: relies on RLS + the caller passing an accountId
 * obtained from listAccounts() (which filters by tenant_id).
 *
 * Status mapping (DB → client galaxy enum):
 *   green  → cleared
 *   amber  → partial
 *   red    → open
 *   null/missing → open
 */
import { useEffect, useState } from "react";
import { supabase } from "./session";
import {
  HELIX_CHECKPOINTS,
  type CheckpointStatus,
  emptyStatusMap,
} from "./helix-checkpoints";
import { ALL_QUESTIONS } from "@shared/questions";

// ── Public row types ──────────────────────────────────────────────────────────

export interface GalaxyAnsweredQuestion {
  id: string;
  text: string;
  answer: string;
  points: number;
}

export interface GalaxyUnansweredQuestion {
  id: string;
  text: string;
  hint: string;
}

export interface HelixGalaxyData {
  statuses: Record<string, CheckpointStatus>;
  answered: Record<number, GalaxyAnsweredQuestion[]>;
  unanswered: Record<number, GalaxyUnansweredQuestion[]>;
  loading: boolean;
  error: string | null;
}

// ── DB enum → client enum ─────────────────────────────────────────────────────

function mapStatus(db: string | null | undefined): CheckpointStatus {
  if (db === "green") return "cleared";
  if (db === "amber") return "partial";
  if (db === "red") return "open";
  return "open";
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useHelixGalaxyData(accountId: number | null): HelixGalaxyData {
  const [statuses, setStatuses] = useState<Record<string, CheckpointStatus>>(
    emptyStatusMap()
  );
  const [answered, setAnswered] = useState<Record<number, GalaxyAnsweredQuestion[]>>({});
  const [unanswered, setUnanswered] = useState<Record<number, GalaxyUnansweredQuestion[]>>({});
  const [loading, setLoading] = useState<boolean>(accountId != null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (accountId == null) {
      setStatuses(emptyStatusMap());
      setAnswered({});
      setUnanswered({});
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const sb = supabase();
        const [cpRes, ansRes] = await Promise.all([
          sb
            .from("account_cp_status")
            .select("cp_number,status")
            .eq("account_id", accountId),
          sb
            .from("account_answers")
            .select("question_key,selected_option,evidence")
            .eq("account_id", accountId),
        ]);

        if (cpRes.error) throw cpRes.error;
        if (ansRes.error) throw ansRes.error;
        if (cancelled) return;

        // Statuses: start from defaults (all open), overlay any DB rows
        const nextStatuses = emptyStatusMap();
        for (const row of cpRes.data ?? []) {
          const cpId = `cp${row.cp_number}`;
          if (cpId in nextStatuses) {
            nextStatuses[cpId] = mapStatus(row.status as string | null);
          }
        }

        // Index answers by question_key for O(1) join
        const ansByKey = new Map<string, { selected: string | null; evidence: string | null }>();
        for (const row of ansRes.data ?? []) {
          ansByKey.set(row.question_key as string, {
            selected: (row.selected_option as string | null) ?? null,
            evidence: (row.evidence as string | null) ?? null,
          });
        }

        // Join the question catalog per CP
        const nextAnswered: Record<number, GalaxyAnsweredQuestion[]> = {};
        const nextUnanswered: Record<number, GalaxyUnansweredQuestion[]> = {};
        for (const cp of HELIX_CHECKPOINTS) {
          const cpIdx = cp.number - 1;
          const cpQuestions = ALL_QUESTIONS.filter((q) => q.cp === cp.number);
          const ansList: GalaxyAnsweredQuestion[] = [];
          const unansList: GalaxyUnansweredQuestion[] = [];

          for (const q of cpQuestions) {
            const a = ansByKey.get(q.key);
            if (a && a.selected) {
              const opt = q.options.find((o) => o.key === a.selected);
              ansList.push({
                id: q.key,
                text: q.label,
                answer: opt?.label ?? a.evidence ?? a.selected,
                points: opt?.points ?? 0,
              });
            } else {
              unansList.push({
                id: q.key,
                text: q.label,
                hint: q.helpText ?? "",
              });
            }
          }

          nextAnswered[cpIdx] = ansList;
          nextUnanswered[cpIdx] = unansList;
        }

        setStatuses(nextStatuses);
        setAnswered(nextAnswered);
        setUnanswered(nextUnanswered);
        setLoading(false);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? "Failed to load helix data");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  return { statuses, answered, unanswered, loading, error };
}
